import React, { useEffect } from "react";

import { downloadUrl, saveBlob } from "@/shared/components/viewer/files";

import { lazyChunk, useChunk } from "./lazyChunk";

/* A picture or a diagram from an answer at full size, in the app's viewer (`ViewerFrame`, the
   one a generated image or a file opens in) with its Download. The viewer's dialog arrives with
   the first one opened. */

const viewer = lazyChunk(() => import("@/shared/components/viewer/ViewerFrame"));

type FullViewProps = {
  title: string;
  /** a file extension: the type icon, and the first word of the line under the name */
  kind: string;
  /** the rest of that line: where it came from */
  about: string;
  /** what Download saves under the title: an address, or the bytes */
  download: string | Blob;
  onClose: () => void;
  /** a picture, drawn as the viewer draws one */
  picture?: { src: string; alt: string };
  /** anything else, centred, scrolling when it is bigger than the viewer */
  children?: React.ReactNode;
};

export default function FullView({ title, kind, about, download, onClose, picture, children }: FullViewProps) {
  const module = useChunk(viewer);
  // a viewer that can't load closes, so the next click tries again
  useEffect(() => void viewer.load().catch(onClose), [onClose]);
  if (!module) return null;
  const { default: ViewerFrame, DownloadButton, ImagePreview } = module;
  const save = () => (typeof download === "string" ? downloadUrl(download, title) : saveBlob(download, title));
  return (
    <ViewerFrame
      title={title}
      icon={kind}
      description={<>{kind.toUpperCase()}<span aria-hidden="true" className="mx-1.5">·</span>{about}</>}
      action={<DownloadButton onClick={save} />}
      onClose={onClose}
    >
      {picture ? (
        <ImagePreview {...picture} />
      ) : (
        <div className="h-full overflow-auto p-4 sm:p-8 flex [justify-content:safe_center] [align-items:safe_center]">{children}</div>
      )}
    </ViewerFrame>
  );
}
